import { useEffect, useRef } from "react";
import { Box, Popper, useTheme } from "@mui/material";
import type { Instance as PopperInstance } from "@popperjs/core";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import { DotLegalButton, DotLegalTooltip } from "@dotlegal/dotlegal-ui-components";
import { SelectionAnchor } from "./useTextSelectionCapture";

const ICON_SIZE = 18;
const GUTTER_GAP = 12;

export interface ISelectionActionButtonProps {
    anchorEl: SelectionAnchor;
    label: string;
    onClick: () => void;
}

export default function SelectionActionButton(props: ISelectionActionButtonProps) {
    const theme = useTheme();
    const popperRef = useRef<PopperInstance>(null);
    const scrollRoot = props.anchorEl.contextElement;

    // Capture phase so the inner scroller is caught too: the PDF list scrolls a child of the viewer
    // container, and a bubbling listener on the container never sees it.
    useEffect(() => {
        if (!scrollRoot) {
            return;
        }
        const handleScroll = () => popperRef.current?.update();
        scrollRoot.addEventListener("scroll", handleScroll, true);
        return () => scrollRoot.removeEventListener("scroll", handleScroll, true);
    }, [scrollRoot]);

    return (
        <Popper
            open
            anchorEl={props.anchorEl}
            popperRef={popperRef}
            // Beside the page rather than over it: the anchor rect spans the page width, so this puts the
            // button out in the margin, clear of the text it acts on.
            placement="right"
            style={{ zIndex: theme.zIndex.tooltip }}
            // The hide modifier below only marks the button with this attribute; nothing renders on it
            // unless we say what being hidden looks like.
            sx={{ "&[data-popper-reference-hidden]": { visibility: "hidden", pointerEvents: "none" } }}
            modifiers={[
                { name: "offset", options: { offset: [0, GUTTER_GAP] } },
                { name: "flip", options: { fallbackPlacements: ["left", "top"] } },
                // The selection scrolls out of view, or its page gets virtualised away, and its rect
                // collapses to nothing. Hiding beats leaving a button floating over unrelated text.
                { name: "hide", enabled: true },
                { name: "preventOverflow", options: { padding: 8 } },
            ]}
        >
            <DotLegalTooltip text={props.label} position="top">
                {/* Keeps the selection alive: without this, pressing down outside the document collapses it
                    before the click lands, and the dialog's focus trap pulls focus off the page. */}
                <Box onMouseDown={(event) => event.preventDefault()} sx={{ display: "inline-flex" }}>
                    <DotLegalButton buttonType="secondary" size="small" onClick={props.onClick}>
                        <FormatQuoteIcon sx={{ fontSize: ICON_SIZE }} />
                    </DotLegalButton>
                </Box>
            </DotLegalTooltip>
        </Popper>
    );
}
